import { updateMaterialReceipt, postReceiptThreadMessage, toThreadPayload } from './materialReceiptService';
import { oversSummary, noteStamp, type ReceiptDraft } from '../data/receiptDraft';
import type { MaterialReceiptRow } from '../types/api';

// The Overs workflow. When a job is marked Complete and more was received than
// ordered (excess stock), flag the receipt for an Overs follow-up (Pending) and
// post the workflow traces + a review notice into the receipt's chat thread, so
// the operator (Wayne) can confirm the excess amount and storage location.
//
// Returns the updated row if an Overs item was created, else null. Written as a
// standalone function so the future MIC/Mediant Jobs-DB sync trigger can invoke
// the same path.
export async function maybeCreateOvers(
  receiptId: string,
  draft: ReceiptDraft,
): Promise<MaterialReceiptRow | null> {
  const jobComplete = draft.jobs.some(j => j.jobStatus === 'Complete');
  const detail = oversSummary(draft);
  if (!jobComplete || !detail) return null;

  // Workflow trace steps — collapse into the activity group when opened in Ace.
  await postReceiptThreadMessage(receiptId, toThreadPayload({
    role: 'agent', content: 'Job marked Complete — running the Overs check (received vs ordered)…', trace: true,
  }));
  await postReceiptThreadMessage(receiptId, toThreadPayload({
    role: 'agent', content: `Excess stock detected: ${detail}.`, trace: true,
  }));

  const updated = await updateMaterialReceipt(receiptId, {
    overs_status: 'Pending',
    note_log: [...draft.noteLog, {
      author: 'Ace', timestamp: noteStamp(),
      text: `Overs flagged — ${detail}. Pending review to confirm the excess amount and storage location.`,
      kind: 'system',
    }],
  });

  // Notify the operator to review.
  await postReceiptThreadMessage(receiptId, toThreadPayload({
    role: 'agent',
    content: `Overs flagged for review — confirm the excess amount and where it's stored, then mark it stored. Excess: ${detail}.`,
  }));

  return updated;
}
